/**
 * @fileoverview 内嵌调试控制台
 * @description 拦截 console.log/warn/error/info，在页面内显示日志
 *   - 通过 URL 参数 ?dev=1 或 localStorage.setItem('DEV_MODE','1') 启用
 *   - 右下角浮动按钮，点击展开/收起控制台面板
 *   - 支持清空日志、复制全部日志
 *   - 显示版本号和 TG 环境信息
 * @module components/DebugConsole
 */
import { useEffect, useRef, useState, useCallback } from 'react';

// ===== 版本号（每次部署更新）=====
const APP_VERSION = 'v20260602-prod';

// ===== 是否为生产环境 =====
const IS_PROD = import.meta.env.PROD;

// ===== 是否启用调试模式 =====
function isDevMode(): boolean {
  // 生产环境完全禁用调试控制台
  if (IS_PROD) return false;
  try {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dev') === '1') {
      localStorage.setItem('DEV_MODE', '1');
      return true;
    }
    if (localStorage.getItem('DEV_MODE') === '1') return true;
    return false;
  } catch {
    return false;
  }
}

type LogLevel = 'log' | 'warn' | 'error' | 'info';

interface LogEntry {
  id: number;
  level: LogLevel;
  time: string;
  msg: string;
}

let logIdCounter = 0;
// 全局日志队列（在组件挂载前也能收集日志）
const globalLogs: LogEntry[] = [];
const listeners: Set<() => void> = new Set();

function formatArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (a === null) return 'null';
      if (a === undefined) return 'undefined';
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack ?? ''}`;
      try {
        return JSON.stringify(a, null, 0);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

// 安装 console 拦截器（全局只安装一次）
let interceptInstalled = false;
function installInterceptor() {
  if (interceptInstalled) return;
  interceptInstalled = true;

  const levels: LogLevel[] = ['log', 'warn', 'error', 'info'];
  levels.forEach((level) => {
    const original = console[level].bind(console);
    (console as any)[level] = (...args: unknown[]) => {
      original(...args);
      const now = new Date();
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;
      const entry: LogEntry = {
        id: ++logIdCounter,
        level,
        time,
        msg: formatArgs(args),
      };
      globalLogs.push(entry);
      // 最多保留 500 条
      if (globalLogs.length > 500) globalLogs.splice(0, globalLogs.length - 500);
      listeners.forEach((fn) => fn());
    };
  });

  // 拦截 TelegramWebviewProxy.postEvent，捕获 TON Connect 发出的 TG 事件
  const tg = (window as any);
  if (tg.TelegramWebviewProxy?.postEvent) {
    const origPostEvent = tg.TelegramWebviewProxy.postEvent.bind(tg.TelegramWebviewProxy);
    tg.TelegramWebviewProxy.postEvent = (eventType: string, eventData: string) => {
      console.info(`[TelegramWebviewProxy.postEvent] ${eventType}`, eventData);
      origPostEvent(eventType, eventData);
    };
  }
  // 捕获未处理的错误
  window.addEventListener('error', (e) => {
    const entry: LogEntry = {
      id: ++logIdCounter,
      level: 'error',
      time: new Date().toLocaleTimeString(),
      msg: `[uncaught] ${e.message} @ ${e.filename}:${e.lineno}`,
    };
    globalLogs.push(entry);
    listeners.forEach((fn) => fn());
  });

  window.addEventListener('unhandledrejection', (e) => {
    const entry: LogEntry = {
      id: ++logIdCounter,
      level: 'error',
      time: new Date().toLocaleTimeString(),
      msg: `[unhandledRejection] ${String(e.reason)}`,
    };
    globalLogs.push(entry);
    listeners.forEach((fn) => fn());
  });
}

/** 诊断 TON Connect DOM 状态 */
function diagnoseTonConnect(): void {
  try {
    // 检查 tc-widget-root
    const widgetRoot = document.getElementById('tc-widget-root');
    console.info(`[DIAG] #tc-widget-root: ${widgetRoot ? 'EXISTS' : 'NOT FOUND'}`);
    if (widgetRoot) {
      console.info(`[DIAG] #tc-widget-root innerHTML length: ${widgetRoot.innerHTML.length}`);
      console.info(`[DIAG] #tc-widget-root children: ${widgetRoot.children.length}`);
    }

    // 检查 tc-root 自定义元素
    const tcRoot = document.querySelector('tc-root');
    console.info(`[DIAG] tc-root element: ${tcRoot ? 'EXISTS' : 'NOT FOUND'}`);
    if (tcRoot) {
      const sr = (tcRoot as any).shadowRoot;
      console.info(`[DIAG] tc-root.shadowRoot: ${sr ? 'EXISTS' : 'NULL'}`);
      if (sr) {
        console.info(`[DIAG] tc-root.shadowRoot.innerHTML length: ${sr.innerHTML.length}`);
      }
    }

    // 检查 customElements
    const tcRootDefined = customElements.get('tc-root');
    console.info(`[DIAG] customElements.get('tc-root'): ${tcRootDefined ? 'DEFINED' : 'NOT DEFINED'}`);

    // 检查 body 中所有 tc- 开头的元素
    const tcElements = document.querySelectorAll('[id^="tc-"], tc-root');
    console.info(`[DIAG] All tc- elements in DOM: ${tcElements.length}`);
    tcElements.forEach((el) => {
      console.info(`[DIAG]   - ${el.tagName} id=${el.id || 'none'}`);
    });

    // 检查 body 直接子元素
    const bodyChildren = Array.from(document.body.children);
    console.info(`[DIAG] document.body direct children: ${bodyChildren.length}`);
    bodyChildren.forEach((el) => {
      console.info(`[DIAG]   - ${el.tagName} id="${el.id}" class="${el.className.toString().substring(0, 50)}"`);
    });

    // 检查 TG WebApp 状态
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp) {
      console.info(`[DIAG] TG WebApp platform: ${tgWebApp.platform}`);
      console.info(`[DIAG] TG WebApp version: ${tgWebApp.version}`);
      console.info(`[DIAG] TG WebApp isExpanded: ${tgWebApp.isExpanded}`);
      console.info(`[DIAG] TG WebApp viewportHeight: ${tgWebApp.viewportHeight}`);
    }

    // 检查 TelegramWebviewProxy
    const twp = (window as any).TelegramWebviewProxy;
    console.info(`[DIAG] TelegramWebviewProxy: ${twp ? 'EXISTS' : 'NOT FOUND'}`);

    // 检查 window.Telegram
    console.info(`[DIAG] window.Telegram: ${(window as any).Telegram ? 'EXISTS' : 'NOT FOUND'}`);

    // 检查 SolidJS 渲染标记
    const solidRoots = document.querySelectorAll('[data-hk]');
    console.info(`[DIAG] SolidJS [data-hk] elements: ${solidRoots.length}`);

  } catch (e: any) {
    console.error('[DIAG] Error during diagnosis:', e?.message);
  }
}

const LEVEL_COLORS: Record<LogLevel, string> = {
  log: '#e0e0e0',
  info: '#64b5f6',
  warn: '#ffb74d',
  error: '#ef5350',
};

export default function DebugConsole() {
  const [enabled] = useState(() => isDevMode());
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  // 安装 console 拦截器 + postMessage 拦截器
  useEffect(() => {
    if (!enabled) return;
    installInterceptor();
    // 同步已有日志
    setLogs([...globalLogs]);
    const update = () => setLogs([...globalLogs]);
    listeners.add(update);
    return () => { listeners.delete(update); };
  }, [enabled]);

  // 自动滚动到底部
  useEffect(() => {
    if (open && autoScrollRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, open]);

  const clearLogs = useCallback(() => {
    globalLogs.splice(0, globalLogs.length);
    setLogs([]);
  }, []);

  const copyLogs = useCallback(() => {
    const text = globalLogs.map((l) => `[${l.time}][${l.level.toUpperCase()}] ${l.msg}`).join('\n');
    navigator.clipboard?.writeText(text).catch(() => {});
  }, []);

  const enableDev = useCallback(() => {
    localStorage.setItem('DEV_MODE', '1');
    window.location.reload();
  }, []);

  const disableDev = useCallback(() => {
    localStorage.removeItem('DEV_MODE');
    window.location.reload();
  }, []);

  const runDiag = useCallback(() => {
    diagnoseTonConnect();
    // 诊断 currentUser.guid
    try {
      const storeKey = Object.keys((window as any).__zustand_stores__ || {});
      console.info('[DIAG] zustand stores:', storeKey.length);
    } catch {}
    // 通过 localStorage 查看登录数据
    try {
      const keys = Object.keys(localStorage);
      console.info('[DIAG] localStorage keys:', keys.join(', '));
      keys.forEach(k => {
        if (k.toLowerCase().includes('user') || k.toLowerCase().includes('auth') || k.toLowerCase().includes('token') || k.toLowerCase().includes('guid')) {
          const v = localStorage.getItem(k);
          console.info(`[DIAG] localStorage[${k}]:`, v?.substring(0, 200));
        }
      });
    } catch (e: any) {
      console.error('[DIAG] localStorage error:', e?.message);
    }
  }, []);

  const filteredLogs = filter
    ? logs.filter((l) => l.msg.toLowerCase().includes(filter.toLowerCase()) || l.level.includes(filter.toLowerCase()))
    : logs;

  // TG 环境信息
  const tgInfo = (() => {
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (!tg) return 'No TG SDK';
      return `platform=${tg.platform} v=${tg.version}`;
    } catch { return 'TG SDK error'; }
  })();

  if (!enabled) {
    // 生产环境完全不渲染任何调试 UI
    if (IS_PROD) return null;
    // 非生产环境显示极小版本标签，点击可启用调试模式
    return (
      <div
        onClick={enableDev}
        style={{
          position: 'fixed', bottom: 4, right: 4, zIndex: 999,
          background: 'rgba(0,0,0,0.5)', color: '#888', fontSize: 9,
          padding: '1px 4px', borderRadius: 3, fontFamily: 'monospace',
          cursor: 'pointer', userSelect: 'none',
        }}
        title="点击启用调试模式"
      >
        {APP_VERSION}
      </div>
    );
  }

  return (
    <>
      {/* 版本号 + 浮动按钮 */}
      <div
        style={{
          position: 'fixed', bottom: 8, right: 8, zIndex: 999,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
        }}
      >
        {/* 版本号标签 */}
        <div style={{
          background: 'rgba(0,0,0,0.8)', color: '#00ff88', fontSize: 10,
          padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace',
        }}>
          {APP_VERSION} | {tgInfo}
        </div>

        {/* 控制台开关按钮 */}
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            background: open ? '#1a1a2e' : '#0d1117',
            border: '1px solid #30363d',
            color: '#58a6ff',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: 'monospace',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {open ? '▼ Console' : '▲ Console'} {logs.length > 0 && (
            <span style={{
              background: logs.some(l => l.level === 'error') ? '#ef5350' : '#30363d',
              color: '#fff', borderRadius: 10, padding: '0 5px', fontSize: 10,
            }}>
              {logs.length}
            </span>
          )}
        </button>
      </div>

      {/* 控制台面板 */}
      {open && (
        <div
          style={{
            position: 'fixed', bottom: 72, right: 0, left: 0, zIndex: 998,
            height: '50vh', maxHeight: 400,
            background: '#0d1117',
            border: '1px solid #30363d',
            borderBottom: 'none',
            display: 'flex', flexDirection: 'column',
            fontFamily: 'monospace',
            fontSize: 11,
          }}
        >
          {/* 工具栏 */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 8px',
            background: '#161b22',
            borderBottom: '1px solid #30363d',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}>
            <span style={{ color: '#58a6ff', fontSize: 11, fontWeight: 600 }}>
              🐛 Debug Console
            </span>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="过滤..."
              style={{
                flex: 1, minWidth: 60, background: '#0d1117', border: '1px solid #30363d',
                color: '#e6edf3', fontSize: 10, padding: '2px 6px', borderRadius: 4,
                outline: 'none',
              }}
            />
            <button onClick={runDiag} style={{ ...btnStyle, color: '#ffd700' }}>诊断DOM</button>
            <button onClick={clearLogs} style={btnStyle}>清空</button>
            <button onClick={copyLogs} style={btnStyle}>复制</button>
            <button onClick={disableDev} style={{ ...btnStyle, color: '#f85149' }}>关闭调试</button>
          </div>

          {/* 日志列表 */}
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
            }}
          >
            {filteredLogs.length === 0 ? (
              <div style={{ color: '#484f58', padding: '8px 12px', fontSize: 11 }}>
                暂无日志。打开充值弹窗后日志会显示在这里。
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div
                  key={log.id}
                  style={{
                    padding: '1px 8px',
                    borderBottom: '1px solid rgba(48,54,61,0.3)',
                    color: LEVEL_COLORS[log.level],
                    wordBreak: 'break-all',
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ color: '#484f58', marginRight: 6 }}>{log.time}</span>
                  {log.level !== 'log' && (
                    <span style={{
                      color: LEVEL_COLORS[log.level],
                      background: 'rgba(255,255,255,0.05)',
                      padding: '0 3px', borderRadius: 2, marginRight: 4, fontSize: 9,
                    }}>
                      {log.level.toUpperCase()}
                    </span>
                  )}
                  {log.msg}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>
        </div>
      )}
    </>
  );
}

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #30363d',
  color: '#8b949e',
  fontSize: 10,
  padding: '2px 6px',
  borderRadius: 4,
  cursor: 'pointer',
  flexShrink: 0,
};
